import { Spinner } from '@/components/Spinner';
import useApiCall from '@/hooks/useApiCall';
import {
  Button,
  ButtonIcon,
  Input,
  Select,
  SelectOption,
  Stack,
  Table,
} from '@data-products-and-ai/react-components';
import {
  TableHeaderType,
  TableRowData,
} from '@data-products-and-ai/react-components/lib/components/DataDisplay/Table/types';
import React, { useEffect, useState } from 'react';
import { TPartLife } from '../../types';
import { TTableSize } from '../PartDetailTables';

type TPartLifeTable = {
  partlife: TPartLife[];
  size?: TTableSize;
  bom: string;
  editable: boolean;
};

type TPartLifeWithEditing = TPartLife & {
  isEditing: boolean;
  publish: boolean;
};

const PartLifeTable = ({
  partlife,
  size = 'normal',
  bom,
  editable = true,
}: TPartLifeTable) => {
  const partLifes: TPartLifeWithEditing[] = partlife.map((item) => ({
    ...item,
    isEditing: false,
    publish: false,
  }));
  const { makeApiCall } = useApiCall<{
    [key: string]: TPartLifeWithEditing[];
  }>();

  const [dynamicValue, setDynamicValue] = useState(partLifes);
  const [dynamicValueMirror, setDynamicValueMirror] = useState(partLifes);

  const headers: TableHeaderType[] = [
    {
      align: 'left',
      title: 'Frame',
      columnWidth: '180px',
    },
    {
      align: 'center',
      title: 'TIT iso [°C]',
    },
    {
      align: 'center',
      title: 'Total Starts [-]',
    },
    {
      align: 'center',
      title: 'Creep Life [kOH]',
    },
    {
      align: 'center',
      title: 'HCF Life [kOH]',
    },
    {
      align: 'center',
      title: 'Int. Oxidation Life [kOH]',
    },
    {
      align: 'center',
      title: 'Status',
    },
    {
      align: 'right',
      title: '',
      columnWidth: '10px',
    },
  ];

  type TEditableCell = {
    id: number;
    str: string;
    partLifeKey: keyof TPartLife;
    type?: 'input' | 'frame' | 'status';
  };

  const setEditing = (id: number, isEditing: boolean) => {
    if (!isEditing) {
      //revert changes in state - cancel was pressed
      setDynamicValueMirror(
        dynamicValue.map((item) => {
          if (item.id === id) {
            return { ...item, isEditing: isEditing, publish: false };
          }
          return item;
        }),
      );
    }
    //change isEditing Flag
    setDynamicValue(
      dynamicValue.map((item) => {
        if (item.id === id) {
          return { ...item, isEditing: isEditing };
        }
        return item;
      }),
    );
  };

  const updateRow = (id: number) => {
    setDynamicValue((prevDynamicValue) => {
      return prevDynamicValue.map((item) => {
        if (item.id === id) {
          return { ...item, publish: true, isEditing: false };
        }
        return item;
      });
    });
  };

  const UpdatePartLife = (partlifeRow: TPartLifeWithEditing) => {
    const update = async () => {
      try {
        return await makeApiCall({
          method: 'PUT',
          useAsFetch: true,
          url: import.meta.env.VITE_APP_API_URL + `/part/${bom}/part_lifes`,
          payload: partlifeRow,
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        console.error('Error occurred:', error);

        throw error;
      }
    };

    update()
      .then((result) => {
        if (result.status && result.status !== 200) {
          alert("ERROR: Row couldn't be updated");
          return;
        }

        setDynamicValue((prevDynamicValue) => {
          return prevDynamicValue.map((item) => {
            if (item.id === partlifeRow.id) {
              return partlifeRow;
            }
            return item;
          });
        });

        /*  setIsEditing(false);
        setDescription(currentDescription); */
      })
      .catch((error) => {
        alert('Something went wrong');
        console.log(error);
      });
  };

  const Insert = (partlifeRow: TPartLifeWithEditing) => {
    const update = async () => {
      try {
        return await makeApiCall({
          method: 'POST',
          useAsFetch: true,
          url: import.meta.env.VITE_APP_API_URL + `/part/${bom}/part_lifes`,
          payload: partlifeRow,
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        console.error('Error occurred:', error);

        throw error;
      }
    };

    update()
      .then((result) => {
        if (result.status && result.status !== 200) {
          alert("ERROR: Row couldn't be updated");
          return;
        }

        if (result.status && result.status !== 200) {
          //error
          setDynamicValue((prev) => prev.filter((item) => item.id !== 0));

          alert("ERROR: Earmark couldn't be inserted");
          return;
        }

        setDynamicValue((prevDynamicValue) => {
          return prevDynamicValue.map((item) => {
            if (item.id === partlifeRow.id) {
              return { ...partlifeRow, id: result.ID, isEditing: false };
            }
            return item;
          });
        });
        setDynamicValueMirror((prevDynamicValue) => {
          return prevDynamicValue.map((item) => {
            if (item.id === partlifeRow.id) {
              return { ...partlifeRow, id: result.ID, isEditing: false };
            }
            return item;
          });
        });
      })
      .catch((error) => {
        alert('Something went wrong');
        console.log(error);
      });
  };

  const Delete = (id: number) => {
    if (confirm('Are you sure you want to delete this row?') === true) {
      const update = async () => {
        try {
          return await makeApiCall({
            method: 'DELETE',
            useAsFetch: true,
            url: import.meta.env.VITE_APP_API_URL + `/part/${bom}/part_lifes`,
            payload: { record_id: id },
            headers: {
              'Content-Type': 'application/json',
            },
          });
        } catch (error) {
          console.error('Error occurred:', error);

          throw error;
        }
      };

      update()
        .then((result) => {
          if (result.status && result.status !== 200) {
            alert("ERROR: Row couldn't be updated");
            return;
          }

          setDynamicValue((prevState) =>
            prevState.filter((item) => item.id !== id),
          );

          /*  setIsEditing(false);
        setDescription(currentDescription); */
        })
        .catch((error) => {
          alert('Something went wrong');
          console.log(error);
        });
    }
  };

  useEffect(() => {
    const publishRow = dynamicValue.find((item) => item.publish === true);
    if (!publishRow) return;

    const editedRow = dynamicValueMirror.find(
      (item) => item.id === publishRow.id,
    );

    if (!editedRow) return;

    if (editedRow.id === 0) {
      Insert(editedRow);
    } else {
      UpdatePartLife(editedRow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicValue]);

  const updateCell = (
    id: number,
    str: string,
    partLifeKey: keyof TPartLife,
  ) => {
    setDynamicValueMirror((prevState) => {
      return prevState.map((item) => {
        if (item.id === id) {
          return { ...item, [partLifeKey]: str };
        }
        return item;
      });
    });
  };

  const EditableCell = ({ type = 'input', ...props }: TEditableCell) => {
    const Comp = () => {
      if (type === 'status') {
        const arrStatus = [
          'none',
          'estimated',
          'preliminary',
          'released',
          'test',
        ];
        return (
          <Select
            size="small"
            defaultValue={props.str}
            id={props.partLifeKey + props.id}
            onChange={(event) =>
              updateCell(props.id, event.target.value, props.partLifeKey)
            }
            placeholder="Status"
          >
            {arrStatus.map((el: string) => {
              return <SelectOption key={el} label={el} value={el} />;
            })}
          </Select>
        );
      }

      if (type === 'frame') {
        const FrameList = () => {
          const { isLoading, error, data, makeApiCall } = useApiCall<{
            [key: string]: string[];
          }>();

          useEffect(() => {
            makeApiCall({
              method: 'GET',
              url: import.meta.env.VITE_APP_API_URL + '/applications',
              headers: {
                'Content-Type': 'application/json',
              },
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, []);

          if (isLoading)
            return <Spinner borderSize={2} size={16} color="#CCCCCC" />;
          if (error) return 'Error';

          if (data) {
            return (
              <div>
                <Select
                  size="small"
                  defaultValue={props.str}
                  id={props.partLifeKey + props.id}
                  onChange={(event) =>
                    updateCell(props.id, event.target.value, props.partLifeKey)
                  }
                  placeholder="Frame"
                >
                  {data.applications.map((el: string) => {
                    return <SelectOption key={el} label={el} value={el} />;
                  })}
                </Select>
              </div>
            );
          }

          return '';
        };

        /* const result = item.frame.substring(0, item.frame.indexOf('('));
          result ? (
            <LinkToPage baseUrl={`/frames/${result}/`} id={item.frame} />
          ) : (
            item.frame
          ) */

        return <FrameList />;
      }

      return (
        <Input
          size="small"
          id={props.partLifeKey + props.id}
          defaultValue={props.str}
          onChange={(event) =>
            updateCell(props.id, event.target.value, props.partLifeKey)
          }
        />
      );
    };

    const row = dynamicValue.find((item) => item.id === props.id);
    if (!row) return <Comp />;

    if (row.publish)
      return <Spinner borderSize={2} size={16} color="#CCCCCC" />;

    return row.isEditing ? <Comp /> : props.str;
  };

  const Cancel = (item: TPartLifeWithEditing) => {
    setEditing(item.id, false);

    if (item.id === 0) {
      setDynamicValue((prev) => prev.filter((el) => el.id !== item.id));
    }
  };

  const EditButton = (item: TPartLifeWithEditing) => {
    if (item.publish) return '';
    return item.isEditing ? (
      <Stack direction="row">
        <ButtonIcon
          icon="IconClose"
          size="extra-small"
          tooltipMessage="Cancel"
          hoverType="color"
          onClick={() => Cancel(item)}
        />

        <ButtonIcon
          hoverType="color"
          icon="IconCheck"
          size="extra-small"
          tooltipMessage="Confirm"
          onClick={() => updateRow(item.id)}
        />
      </Stack>
    ) : (
      <Stack direction="row" gap={5}>
        <ButtonIcon
          hoverType="color"
          icon="IconEdit"
          size="extra-small"
          tooltipMessage="Edit"
          disabled={
            dynamicValue.filter((item) => item.isEditing === true).length > 0
          }
          onClick={() => setEditing(item.id, true)}
        />
        <ButtonIcon
          hoverType="color"
          icon="IconTrash"
          size="extra-small"
          tooltipMessage="Delete"
          disabled={
            dynamicValue.filter((item) => item.isEditing === true).length > 0
          }
          onClick={() => Delete(item.id)}
        />
      </Stack>
    );
  };
  const rows: TableRowData[] = dynamicValue.map((item, index: number) => {
    return {
      data: {
        frame: (
          <EditableCell
            key={item.id + 'frame'}
            id={item.id}
            str={item.frame}
            partLifeKey="frame"
            type="frame"
          />
        ),

        tit_iso: (
          <EditableCell
            key={item.id + 'tit_ison'}
            id={item.id}
            str={item.tit_iso}
            partLifeKey="tit_iso"
          />
        ),
        total_starts: (
          <EditableCell
            id={item.id}
            str={item.total_starts}
            partLifeKey="total_starts"
          />
        ),
        hcf: <EditableCell id={item.id} str={item.hcf} partLifeKey="hcf" />,
        int_oxidation: (
          <EditableCell
            id={item.id}
            str={item.int_oxidation}
            partLifeKey="int_oxidation"
          />
        ),
        ext_oxidation: (
          <EditableCell
            id={item.id}
            str={item.ext_oxidation}
            partLifeKey="ext_oxidation"
          />
        ),
        status: (
          <EditableCell
            id={item.id}
            str={item.status}
            partLifeKey="status"
            type="status"
          />
        ),
        edit: editable ? <EditButton {...item} /> : '',
      },
      id: `row_${index}`,
    };
  });

  const memoizedRows = React.useMemo(() => {
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicValue]);

  const memoizedTable = React.useMemo(() => {
    return (
      <Table
        headers={headers}
        rows={memoizedRows}
        headerVerticalAlign="bottom"
        indentation={0}
        typographyTag={
          size === 'normal'
            ? 'textdefault'
            : size === 'extra-small'
            ? 'textextrasmall'
            : 'textsmall'
        }
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedRows]);

  const addNewRow = () => {
    setDynamicValue((prev) => [
      ...prev,
      {
        id: 0,
        frame: '',
        tit_iso: '',
        total_starts: '',
        creep: '',
        hcf: '',
        int_oxidation: '',
        ext_oxidation: '',
        status: '',
        isEditing: true,
        publish: false,
      },
    ]);
    setDynamicValueMirror((prev) => [
      ...prev,
      {
        id: 0,
        frame: '',
        tit_iso: '',
        total_starts: '',
        creep: '',
        hcf: '',
        int_oxidation: '',
        ext_oxidation: '',
        status: '',
        isEditing: true,
        publish: false,
      },
    ]);
  };

  return (
    <>
      {memoizedTable}

      {editable && (
        <div key="MaintenanceAdd" style={{ marginTop: 10, marginBottom: 10 }}>
          <Button
            type="primaryOutline"
            size="small"
            disabled={
              dynamicValue.filter((item) => item.isEditing === true).length > 0
            }
            onClick={() => addNewRow()}
          >
            Add new row
          </Button>
        </div>
      )}
    </>
  );
};

export default PartLifeTable;
